import React, { useState, useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { useDataAuth } from '../../hooks/useDataAuth';
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import Paper from "@mui/material/Paper";
import Slide from "@mui/material/Slide";
import Box from "@mui/material/Box";

import SupportAgentRounded from "@mui/icons-material/SupportAgentRounded";
import Person from "@mui/icons-material/Person";
import Close from "@mui/icons-material/Close";
import Send from "@mui/icons-material/Send";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { sendRequest } from "../../services/agents.service";
import { agentId, aliasId, flowCallAgentWidget } from "../../configs/params";

export default function ChatWidget({ isOpen, onClose }) {
    const [{ userSession }] = useDataAuth();
    // image lightbox state
    const [openImage, setOpenImage] = useState(false);
    const [imageSrc, setImageSrc] = useState("");
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const chatEndRef = useRef(null);
    const audioRef = useRef(new Audio("/sounds/airplane_ding.mp3"));
    const sessionIdRef = useRef(`${userSession.username.split("@")[0]}-${Date.now()}`);


    // Scroll to bottom when new messages arrive
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);


    // Normalize custom link tokens coming from KB/agent:
    // - "<url-https://a.com>" -> "<https://a.com>"
    // - "[url-https://a.com](https://a.com)" -> "[https://a.com](https://a.com)"
    // - "url-https://a.com" -> "https://a.com"
    const normalizeMarkdownLinks = (md) => {
        if (!md || typeof md !== "string") return md;
        return md
            .replace(/<url-([^>]+)>/g, "<$1>")
            .replace(/\[url-([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, "[$1]($2)")
            .replace(/url-(https?:\/\/[^\s)]+)/g, "$1");
    };

    const handleSend = async () => {
        try {
            if (!input.trim()) return;

            // --- Cosmetic Chat update
            const userMsg = { sender: "user", text: input.trim() };
            setMessages((prev) => [...prev, userMsg]);
            setInput("");

            // --- Replace AWS Bedrock call ---
            // const agentMsg = await fakeAgentResponse(input.trim());
            const json = {
                id_flow: flowCallAgentWidget,
                message: input.trim(),
                agent_id: agentId,
                agent_alias_id: aliasId,
                session_id: sessionIdRef.current
            }
            // console.log("json", json)
            const agentMsg = await sendRequest(userSession.token, json)
            // console.log("agentMsg", agentMsg)

            let markdown = "";
            try {
                // agentMsg.data may be a stringified JSON or already an object
                let parsedPayload = null;

                if (agentMsg && agentMsg.data) {
                    if (typeof agentMsg.data === "object") {
                        parsedPayload = agentMsg.data;
                    } else if (typeof agentMsg.data === "string") {
                        // Try normal parse first
                        try {
                            parsedPayload = JSON.parse(agentMsg.data);
                        } catch (err) {
                            // JSON.parse failed (likely due to literal newlines / control chars).
                            // Escape newlines and retry.
                            try {
                                const escaped = agentMsg.data.replace(/\r\n|\n|\r/g, "\\n");
                                parsedPayload = JSON.parse(escaped);
                            } catch (err2) {
                                // Last resort: evaluate as JS expression (risky but useful for malformed responses).
                                try {
                                    /* eslint-disable no-new-func */
                                    parsedPayload = Function('"use strict";return (' + agentMsg.data + ")")();
                                } catch (err3) {
                                    // console.error("All parsing attempts failed:", err, err2, err3);
                                    parsedPayload = null;
                                }
                            }
                        }
                    }
                } else if (agentMsg && typeof agentMsg === "string") {
                    // whole response may be a stringified JSON
                    parsedPayload = JSON.parse(agentMsg);
                }

                // prefer `response` then `message`
                markdown = parsedPayload?.response ?? parsedPayload?.message ?? "";

                // normalize weird 'url-' tokens produced by KB/agent
                markdown = normalizeMarkdownLinks(markdown);

            } catch (err) {
                // console.error("Failed to parse agent response data:", err);
            }
            // console.log("markdown: ", markdown);
            setMessages((prev) => [...prev, { sender: "agent", markdown }]);

            // setMessages((prev) => [ ...prev, { sender: "agent", markdown } ]);

            // Play sound when agent replies
            // console.log("Playing sound");
            audioRef.current.play().catch(() => { });
        } catch (error) {
            // console.error("Error sending message:", error);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Mock replace with actual Bedrock API call
    const fakeAgentResponse = (msg) =>
        new Promise((resolve) =>
            setTimeout(
                () =>
                    resolve({
                        sender: "agent",
                        text: `Echo: ${msg}`,
                    }),
                1000
            )
        );

    return (
        <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
            <Paper
                elevation={8}
                sx={{
                    position: "fixed",
                    bottom: 24,
                    right: 24,
                    width: 340,
                    height: 460,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    overflow: "hidden",
                    zIndex: 1300,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1}>
                        <SupportAgentRounded fontSize="small" />
                        <Typography variant="subtitle1" fontWeight="bold">
                            Asistente IA
                        </Typography>
                    </Box>
                    <IconButton size="small" color="inherit" onClick={onClose}>
                        <Close />
                    </IconButton>
                </Box>

                {/* Chat Area */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        backgroundColor: "#f4f6f8",
                        p: 2,
                    }}
                >
                    {messages.map((msg, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: "flex",
                                justifyContent:
                                    msg.sender === "user" ? "flex-end" : "flex-start",
                                mb: 1.5,
                            }}
                        >
                            {msg.sender === "agent" && (
                                <Avatar
                                    sx={{
                                        bgcolor: "primary.main",
                                        width: 28,
                                        height: 28,
                                        mr: 1,
                                    }}
                                >
                                    <SupportAgentRounded fontSize="small" />
                                </Avatar>
                            )}

                            <Box
                                sx={{
                                    bgcolor:
                                        msg.sender === "user" ? "grey.300" : "primary.main",
                                    color:
                                        msg.sender === "user"
                                            ? "text.primary"
                                            : "primary.contrastText",
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 2,
                                    maxWidth: "70%",
                                    wordWrap: "break-word",
                                }}
                            >
                                {/* <Typography variant="body2">{ msg.text }</Typography> */}
                                {msg.markdown ? (
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            p: ({ node, ...props }) => (
                                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "inherit" }} component="div" {...props} />
                                            ),
                                            // img: ({ src, alt }) => (
                                            //     <Box component="img" src={src} alt={alt} sx={{ maxWidth: "100%", mt: 1, borderRadius: 1 }} />
                                            // ),
                                            img: ({ src, alt }) => (
                                                <Box
                                                    component="img"
                                                    src={src}
                                                    alt={alt}
                                                    sx={{ maxWidth: "100%", mt: 1, borderRadius: 1, cursor: "pointer" }}
                                                    onClick={() => {
                                                        setImageSrc(src);
                                                        setOpenImage(true);
                                                    }}
                                                />
                                            ),
                                            // a: ({ href, children }) => (
                                            //     <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                                            //         {children}
                                            //     </a>
                                            // ),
                                            a: ({ href, children }) => {
                                                // fallback: if href is empty, try to extract url from link text
                                                let link = href;
                                                if (!link) {
                                                    const text = Array.isArray(children) ? children.map(c => (typeof c === "string" ? c : (c?.props?.children ?? ""))).join("") : (typeof children === "string" ? children : "");
                                                    const m = text.match(/(https?:\/\/[^\s)]+)/);
                                                    if (m) link = m[0];
                                                }
                                                return (
                                                    <a href={link || "#"} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                                                        {children}
                                                    </a>
                                                );
                                            },
                                        }}
                                    >
                                        {msg.markdown}
                                    </ReactMarkdown>
                                ) : (
                                    <Typography variant="body2">{msg.text}</Typography>
                                )}
                            </Box>

                            {msg.sender === "user" && (
                                <Avatar
                                    sx={{
                                        bgcolor: "grey.500",
                                        width: 28,
                                        height: 28,
                                        ml: 1,
                                    }}
                                >
                                    <Person fontSize="small" />
                                </Avatar>
                            )}
                        </Box>
                    ))}
                    <div ref={chatEndRef} />
                    {/* Image preview dialog */}
                    <Dialog
                        open={openImage}
                        onClose={() => setOpenImage(false)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogContent sx={{ p: 0, display: "flex", justifyContent: "center", bgcolor: "background.default" }}>
                            <Box component="img" src={imageSrc} alt="" sx={{ maxWidth: "100%", maxHeight: "80vh" }} />
                        </DialogContent>
                    </Dialog>
                </Box>

                <Divider />

                {/* Input area Chat */}
                <Box sx={{ display: "flex", alignItems: "center", p: 1.5 }}>
                    <TextField
                        fullWidth
                        multiline
                        maxRows={3}
                        variant="outlined"
                        size="small"
                        placeholder="Escribe tu pregunta..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        sx={{ flex: 1, mr: 1 }}
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSend}
                        sx={{ bgcolor: "primary.light", color: "white" }}
                    >
                        <Send fontSize="small" />
                    </IconButton>
                </Box>
            </Paper>
        </Slide>
    );
}
